// Jun-2026 — WP-A: Daily BOQ Work Progress UI. Records executed quantities
// against the CURRENT Approved BOQ baseline (D1: version always displayed;
// no cross-version carry-forward). Commercial values are masked SERVER-side
// for users without boq_commercials — the UI only reflects what it receives.
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, FileSpreadsheet, Undo2, History, Plus, TrendingUp, Camera, Search, CalendarClock, CalendarX } from "lucide-react";
import { EvidencePhotos } from "@/components/EvidencePhotos";
import LabourAttendanceTab from "@/components/LabourAttendanceTab";
import LabourPaymentsTab from "@/components/LabourPaymentsTab";
import DprTab from "@/components/DprTab";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const today = () => new Date().toLocaleDateString("en-CA");
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const DueStatusBadge = ({ endDate }) => {
  if (!endDate) return null;
  const days = Math.round((new Date(endDate) - new Date(today())) / 86400000);
  if (Number.isNaN(days)) return null;
  const over = days < 0;
  const n = Math.abs(days);
  const Icon = over ? CalendarX : CalendarClock;
  return (
    <span data-testid="wp-due-badge" className={`inline-flex items-center gap-1.5 rounded px-2 py-1 border font-bold ${over ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}>
      <Icon size={12} />
      {over ? `Overdue by ${n} day${n === 1 ? "" : "s"}` : days === 0 ? "Due today" : `Within due · ${n} day${n === 1 ? "" : "s"} left`}
      <span className="font-mono font-normal opacity-70">ends {endDate}</span>
    </span>
  );
};

function EntryModal({ project, lines, initialLine, onClose, onPosted }) {
  const [date, setDate] = useState(today());
  const [backdateReason, setBackdateReason] = useState("");
  const [periodReason, setPeriodReason] = useState("");
  const [needPeriodReason, setNeedPeriodReason] = useState(false);
  const [rows, setRows] = useState([{ boq_line_id: initialLine?.boq_line_id || "", qty_today: "", remark: "", key: newKey() }]);
  const [busy, setBusy] = useState(false);
  const backdated = date && date < today();
  const pStart = (project?.project_start_date || "").slice(0, 10);
  const pEnd = (project?.project_end_date || "").slice(0, 10);
  const outsidePeriod = needPeriodReason ||
    (!!date && ((pStart && date < pStart) || (pEnd && date > pEnd)));
  const eligible = lines.filter((l) => l.progressable);

  const setRow = (i, patch) => setRows(rows.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    for (const [i, r] of rows.entries()) {
      if (!r.boq_line_id) return toast.error(`Row ${i + 1}: select a BOQ element`);
      if (!r.qty_today || Number(r.qty_today) <= 0) return toast.error(`Row ${i + 1}: enter a positive quantity`);
      if (!r.remark.trim()) return toast.error(`Row ${i + 1}: work-update remark is mandatory`);
    }
    if (outsidePeriod && !periodReason.trim())
      return toast.error("Exception reason is mandatory — the progress date is outside the project period");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/work-progress/entries`, {
        progress_date: date, backdate_reason: backdateReason.trim(),
        period_exception_reason: periodReason.trim(),
        lines: rows.map((r) => ({ boq_line_id: r.boq_line_id, qty_today: Number(r.qty_today), remark: r.remark.trim(), idempotency_key: r.key })),
      });
      toast.success(`Progress recorded for ${rows.length} line(s)`);
      onPosted?.(); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to record progress";
      if (String(msg).toLowerCase().includes("outside the project period")) setNeedPeriodReason(true);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="wp-entry-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Daily Work Progress</div>
          <button data-testid="wp-entry-modal-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Progress Date *</span>
              <input data-testid="wp-date-input" type="date" max={today()} className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            {backdated && (
              <label className="space-y-1"><span className="font-bold text-amber-700">Backdating Reason *</span>
                <input data-testid="wp-backdate-reason" className={inputCls} value={backdateReason} onChange={(e) => setBackdateReason(e.target.value)} />
              </label>
            )}
          </div>
          {outsidePeriod && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-1.5" data-testid="wp-period-exception-box">
              <div className="text-amber-800 font-bold">
                {date} is outside the project period ({pStart || "—"} → {pEnd || "—"}). An explicit exception reason is required to post.
              </div>
              <label className="block space-y-1"><span className="font-bold text-amber-700">Period Exception Reason *</span>
                <input data-testid="wp-period-reason" className={inputCls} value={periodReason}
                  onChange={(e) => setPeriodReason(e.target.value)}
                  placeholder="Why is progress being recorded outside the project period?" />
              </label>
            </div>
          )}
          {rows.map((r, i) => {
            const sel = eligible.find((l) => l.boq_line_id === r.boq_line_id);
            return (
              <div key={r.key} className="border border-slate-200 rounded p-3 space-y-2" data-testid={`wp-entry-row-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1"><span className="font-bold text-slate-600">BOQ Element *</span>
                    <select data-testid={`wp-line-select-${i}`} className={inputCls} value={r.boq_line_id} onChange={(e) => setRow(i, { boq_line_id: e.target.value })}>
                      <option value="">— select —</option>
                      {eligible.map((l) => (
                        <option key={l.boq_line_id} value={l.boq_line_id}>{l.element_code} — {(l.name || "").slice(0, 50)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="font-bold text-slate-600">Qty Completed Today {sel ? `(${sel.uom_name})` : ""} *</span>
                    <input data-testid={`wp-qty-input-${i}`} type="number" min="0" step="any" className={inputCls} value={r.qty_today} onChange={(e) => setRow(i, { qty_today: e.target.value })} />
                    {sel && <span className="text-[10px] text-slate-400">BOQ {sel.boq_quantity} · done {sel.cumulative_qty} · {sel.progress_pct ?? 0}%</span>}
                  </label>
                </div>
                <label className="block space-y-1"><span className="font-bold text-slate-600">Work-update Remark *</span>
                  <textarea data-testid={`wp-remark-input-${i}`} className={`${inputCls} h-12`} placeholder="What work was executed today?" value={r.remark} onChange={(e) => setRow(i, { remark: e.target.value })} />
                </label>
                {rows.length > 1 && (
                  <button data-testid={`wp-remove-row-${i}`} onClick={() => setRows(rows.filter((_, ix) => ix !== i))} className="text-orange-700 text-[10px] hover:underline">Remove line</button>
                )}
              </div>
            );
          })}
          <button data-testid="wp-add-row-btn" onClick={() => setRows([...rows, { boq_line_id: "", qty_today: "", remark: "", key: newKey() }])}
            className="inline-flex items-center gap-1 text-sky-700 font-bold hover:underline"><Plus size={12} /> Add another BOQ line</button>
          <div className="text-[10px] text-slate-400">Posts immediately against the current Approved BOQ baseline. Cumulative progress may exceed 100%. Corrections are append-only reversals.</div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
            <button data-testid="wp-entry-submit-btn" disabled={busy} onClick={submit}
              className="px-4 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50">{busy ? "Posting…" : "Post Progress"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReverseModal({ project, entry, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reversal reason is mandatory");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/work-progress/entries/${entry.id}/reverse`, { reason: reason.trim() });
      toast.success("Progress entry reversed");
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reversal failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="wp-reverse-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Reverse {entry.element_code} · {entry.qty_today} {entry.uom_name}?</div>
        <div className="text-slate-500">Append-only correction: a compensating row is added and the original entry is retained with full history.</div>
        <textarea data-testid="wp-reverse-reason" className={`${inputCls} h-16`} placeholder="Reason (mandatory)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <button data-testid="wp-reverse-confirm" disabled={busy} onClick={submit}
            className="px-4 py-1.5 bg-orange-600 text-white font-bold rounded disabled:opacity-50">{busy ? "Reversing…" : "Reverse"}</button>
        </div>
      </div>
    </div>
  );
}

// UX-1 — append-only site photographs for one daily WP entry (max 6)
function WpPhotosModal({ project, entry, canCreate, onClose, onChanged }) {
  const frozen = entry.status === "Reversed";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="wp-photos-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-5 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900">Site Photographs — {entry.element_code} · {entry.progress_date}</div>
          <button data-testid="wp-photos-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {frozen && (
          <div className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1" data-testid="wp-photos-frozen-note">
            Entry is Reversed — new uploads are frozen; existing photos remain visible as historical evidence.
          </div>
        )}
        <EvidencePhotos title="Site Photographs" photos={entry.photos || []} max={6}
          canUpload={!frozen && canCreate}
          uploadUrl={`/projects/${project.id}/work-progress/entries/${entry.id}/photos`}
          photoUrl={(id) => `/projects/${project.id}/work-progress/entries/${entry.id}/photos/${id}`}
          onChanged={onChanged} testidPrefix="wp-photos" />
      </div>
    </div>
  );
}

function LineHistoryModal({ project, line, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/projects/${project.id}/work-progress/lines/${line.boq_line_id}/history`)
      .then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load history"));
  }, [project.id, line.boq_line_id]);
  const vv = !!data?.commercials_visible;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="wp-line-history-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">{line.element_code} — {line.name} · history</div>
          <button data-testid="wp-line-history-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 text-xs space-y-2">
          <div className="text-slate-500">Cumulative: <b>{data?.cumulative_qty ?? "…"} {line.uom_name}</b> of {line.boq_quantity} · {line.progress_pct ?? 0}%</div>
          <div className="overflow-x-auto"><table className="w-full min-w-[680px] text-xs" data-testid="wp-line-history-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Kind</th>
                <th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Prev → New</th>
                {vv && <th className="px-2 py-1.5 text-right">Amount (ex-GST)</th>}
                <th className="px-2 py-1.5 text-left">Remark</th><th className="px-2 py-1.5 text-left">By</th><th className="px-2 py-1.5 text-left">Status</th></tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{e.progress_date}</td>
                  <td className="px-2 py-1.5">{e.entry_kind === "reversal" ? <span className="text-orange-700 font-bold">Reversal</span> : e.entry_kind === "carry_forward" ? <span className="text-sky-700 font-bold" data-testid={`wp-hist-cf-${e.id}`}>Carried Fwd</span> : "Progress"}</td>
                  <td className="px-2 py-1.5 text-right font-bold">{e.qty_delta > 0 ? "+" : ""}{e.qty_delta}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{e.previous_cumulative_qty} → {e.new_cumulative_qty}</td>
                  {vv && <td className="px-2 py-1.5 text-right">{inr(e.amount_delta_ex_gst)}</td>}
                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={e.remark}>{e.remark}</td>
                  <td className="px-2 py-1.5">{e.created_by}</td>
                  <td className="px-2 py-1.5">{e.status === "Reversed" ? <span className="text-orange-700 font-bold">Reversed</span> : e.status}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

const ScopeSplitLines = ({ split, render, testid }) => {
  if (!split) return null;
  return (
    <div className="mt-0.5 mb-0.5 space-y-0.5 text-[10px]" data-testid={testid}>
      <div className="text-emerald-800"><b>Approved:</b> {render(split["Approved"])}</div>
      <div className="text-amber-700"><b>Not Approved:</b> {render(split["Not Approved"])}</div>
    </div>
  );
};

export default function ProjectWorkProgressTab({ project }) {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("work_progress", "create");
  const canReverse = hasPerm("work_progress", "delete");
  const canAttendance = hasPerm("labour_attendance", "view");
  const canLabourPay = hasPerm("labour_payment", "view");
  const canDpr = hasPerm("project_dpr", "view");
  const [view, setView] = useState("lines");
  const [linesData, setLinesData] = useState({ baseline: null, rows: [], commercials_visible: false });
  const [summary, setSummary] = useState(null);
  const [reg, setReg] = useState({ rows: [], total: 0 });
  const [labour, setLabour] = useState(null);
  const [regPage, setRegPage] = useState(1);
  const [flt, setFlt] = useState({ date_from: "", date_to: "", q: "" });
  const [entryModal, setEntryModal] = useState(null);
  const [rev, setRev] = useState(null);
  const [hist, setHist] = useState(null);
  const [photosFor, setPhotosFor] = useState(null);
  const [err, setErr] = useState("");
  const limit = 25;
  const [lineQ, setLineQ] = useState("");
  const filteredLines = useMemo(() => {
    const s = lineQ.trim().toLowerCase();
    const all = linesData?.rows || [];
    if (!s) return all;
    return all.filter((l) =>
      [l.element_code, l.name, l.description, l.uom_name, l.client_scope_status, l.line_type]
        .some((f) => String(f || "").toLowerCase().includes(s)));
  }, [linesData, lineQ]);
  const linePager = usePager(filteredLines, 25);
  const [labourQ, setLabourQ] = useState("");
  const filteredLabour = useMemo(() => {
    const s = labourQ.trim().toLowerCase();
    const all = labour || [];
    if (!s) return all;
    return all.filter((a) =>
      [a.labour_code, a.name, a.mobile, a.skill_category, a.labour_type, a.status]
        .some((f) => String(f || "").toLowerCase().includes(s)));
  }, [labour, labourQ]);
  const labourPager = usePager(filteredLabour, 25);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        api.get(`/projects/${project.id}/work-progress/lines`, { params: { limit: 200 } }),
        api.get(`/projects/${project.id}/work-progress/summary`, {
          params: { date_from: flt.date_from || undefined, date_to: flt.date_to || undefined } }),
      ]);
      setLinesData(l.data || {}); setSummary(s.data || null); setErr("");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load Work Progress"); }
  }, [project.id, flt.date_from, flt.date_to]);

  const loadReg = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/work-progress/entries`, { params: {
        page: regPage, limit, q: flt.q || undefined,
        date_from: flt.date_from || undefined, date_to: flt.date_to || undefined } });
      setReg(r.data || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load history"); }
  }, [project.id, regPage, flt]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "history") loadReg(); }, [view, loadReg]);
  useEffect(() => {
    // WP-B: limited, masked assigned-labour panel (never Aadhaar/address)
    if (view === "labour" && labour === null) {
      api.get(`/projects/${project.id}/assigned-labour`)
        .then((r) => setLabour(r.data.rows || []))
        .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load assigned labour"); setLabour([]); });
    }
  }, [view, labour, project.id]);

  const doExport = async () => {
    try {
      const r = await api.get(`/projects/${project.id}/work-progress-export`, {
        responseType: "blob",
        params: { date_from: flt.date_from || undefined, date_to: flt.date_to || undefined } });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = `WP_${project.project_code}.xlsx`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed (Work Progress export permission required)"); }
  };

  // Aug-2026 (user report) — refresh is now view-aware: it also reloads the
  // Assigned Labour panel and remounts the Attendance / Labour Payments /
  // DPR child tabs (each owns its own data), with a visible confirmation.
  const [childKey, setChildKey] = useState(0);
  const refreshAll = () => {
    load();
    if (view === "history") loadReg();
    if (view === "labour") setLabour(null);
    if (["attendance", "labpay", "dpr"].includes(view)) setChildKey((k) => k + 1);
    toast.success("Work Progress refreshed");
  };
  const vv = !!linesData.commercials_visible;
  const bl = summary?.baseline || linesData.baseline;

  if (err) return <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 text-xs text-amber-800" data-testid="wp-error-notice">{err}</div>;

  return (
    <div className="space-y-3" data-testid="wp-tab">
      {/* D1 — the baseline version is always displayed */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs flex items-center gap-2 flex-wrap" data-testid="wp-baseline-banner">
          {bl ? (
            <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1">
              <TrendingUp size={12} /> Progress against <b>BOQ V{bl.version_number}</b> (Approved)
              <span className="text-emerald-700 font-mono">{bl.boq_number}</span>
            </span>
          ) : (
            <span className="bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">No Approved BOQ baseline — progress cannot be recorded.</span>
          )}
          <DueStatusBadge endDate={project.project_end_date} />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="wp-refresh-btn" onClick={refreshAll} className="p-1.5 text-slate-500 hover:text-slate-800"><RefreshCw size={14} /></button>
          <button data-testid="wp-export-btn" onClick={doExport}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
            <FileSpreadsheet size={13} /> Export Excel
          </button>
          {canCreate && bl && (
            <button data-testid="wp-daily-update-btn" onClick={() => setEntryModal({})}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-emerald-700 text-white rounded hover:bg-emerald-800">
              <Plus size={13} /> Daily Update
            </button>
          )}
        </div>
      </div>

      {/* summary cards — figures split by Client Scope (Approved / Not Approved) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="border-2 border-emerald-200 bg-emerald-50/60 rounded-lg p-3" data-testid="wp-card-overall">
          <div className="text-[11px] uppercase font-bold text-emerald-700">Overall Progress</div>
          <div className="text-2xl font-black text-emerald-900">{summary?.overall_progress_pct != null ? `${summary.overall_progress_pct}%` : "—"}</div>
          <ScopeSplitLines split={summary?.scope_split} testid="wp-split-overall"
            render={(s) => (s?.progress_pct != null ? `${s.progress_pct}%` : "—")} />
          <div className="text-[10px] text-emerald-600/70">value-weighted · uncapped</div>
        </div>
        <div className="border-2 border-sky-200 bg-sky-50/60 rounded-lg p-3" data-testid="wp-card-value">
          <div className="text-[11px] uppercase font-bold text-sky-700">Work Value Completed</div>
          <div className="text-2xl font-black text-sky-900">{vv ? inr(summary?.executed_value_ex_gst) : "•••"}</div>
          {vv && <ScopeSplitLines split={summary?.scope_split} testid="wp-split-value"
            render={(s) => `${inr(s?.executed_value_ex_gst)} of ${inr(s?.total_boq_value_ex_gst)}`} />}
          <div className="text-[10px] text-sky-600/70">{vv ? `of ${inr(summary?.total_boq_value_ex_gst)} ex-GST` : "requires commercial permission"}</div>
        </div>
        <div className="border-2 border-orange-200 bg-orange-50/60 rounded-lg p-3" data-testid="wp-card-today">
          <div className="text-[11px] uppercase font-bold text-orange-700">Today's Progress</div>
          <div className="text-2xl font-black text-orange-900">{summary?.today_qty_net ?? 0}</div>
          <ScopeSplitLines split={summary?.scope_split} testid="wp-split-today"
            render={(s) => `qty ${s?.today_qty_net ?? 0}${vv && s?.today_value_ex_gst != null ? ` · ${inr(s.today_value_ex_gst)}` : ""}`} />
          <div className="text-[10px] text-orange-600/70">net qty (all lines) · {summary?.today_date}</div>
        </div>
        <div className="border-2 border-violet-200 bg-violet-50/60 rounded-lg p-3" data-testid="wp-card-range">
          <div className="text-[11px] uppercase font-bold text-violet-700">Date Range</div>
          <div className="flex flex-wrap gap-1 mt-1">
            <input data-testid="wp-range-from" type="date" className="border border-violet-300 bg-white rounded px-1 py-0.5 text-[10px] flex-1 min-w-[105px]" value={flt.date_from}
              onChange={(e) => { setFlt({ ...flt, date_from: e.target.value }); setRegPage(1); }} />
            <input data-testid="wp-range-to" type="date" className="border border-violet-300 bg-white rounded px-1 py-0.5 text-[10px] flex-1 min-w-[105px]" value={flt.date_to}
              onChange={(e) => { setFlt({ ...flt, date_to: e.target.value }); setRegPage(1); }} />
          </div>
          <div className="text-[10px] text-violet-700 mt-1 font-bold" data-testid="wp-range-result">
            {summary?.range ? `qty ${summary.range.qty_net} · ${summary.range.entry_count} entries${vv && summary.range.range_value_ex_gst != null ? ` · ${inr(summary.range.range_value_ex_gst)}` : ""}` : "pick a range"}
          </div>
        </div>
      </div>

      {/* inner view switch */}
      <div className="flex items-center gap-1 border-b border-slate-200 text-sm flex-wrap">
        {[["lines", "BOQ Lines", "border-sky-600 text-sky-800 bg-sky-50"],
          ["history", "Progress History", "border-violet-600 text-violet-800 bg-violet-50"],
          ["labour", "Assigned Labour", "border-teal-600 text-teal-800 bg-teal-50"],
          ...(canAttendance ? [["attendance", "Labour Attendance", "border-amber-600 text-amber-800 bg-amber-50"]] : []),
          ...(canLabourPay ? [["labpay", "Labour Payments", "border-rose-600 text-rose-800 bg-rose-50"]] : []),
          ...(canDpr ? [["dpr", "Generate DPR", "border-orange-600 text-orange-800 bg-orange-50"]] : [])].map(([k, label, activeCls]) => (
          <button key={k} data-testid={`wp-view-${k}`} onClick={() => setView(k)}
            className={`px-4 py-2 font-bold border-b-[3px] -mb-px rounded-t-md transition-colors ${view === k ? activeCls : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {view === "lines" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="wp-lines-search" className="border border-slate-300 rounded pl-7 pr-2 py-1.5 text-xs w-72"
                placeholder="Search element code / name / UOM / scope…" value={lineQ}
                onChange={(e) => { setLineQ(e.target.value); linePager.setPage(1); }} />
            </div>
            {lineQ && (
              <span className="text-[11px] text-slate-500 font-bold" data-testid="wp-lines-match-count">
                {filteredLines.length} of {(linesData.rows || []).length} lines
              </span>
            )}
          </div>
          <div className="border border-slate-200 rounded overflow-x-auto">
          <table className="w-full min-w-[880px] text-xs" data-testid="wp-lines-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Element</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-left">Client Scope</th>
                <th className="px-3 py-2 text-right">BOQ Qty</th>
                <th className="px-3 py-2 text-right">Executed</th>
                <th className="px-3 py-2 text-right">Remaining</th>
                <th className="px-3 py-2 text-right">Progress %</th>
                {vv && <th className="px-3 py-2 text-right">Rate</th>}
                {vv && <th className="px-3 py-2 text-right">Work Value</th>}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredLines.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400" data-testid="wp-lines-empty">{(linesData.rows || []).length === 0 ? "No BOQ lines in the approved baseline." : "No lines match your search."}</td></tr>
              )}
              {linePager.slice.map((l) => (
                <tr key={l.boq_line_id} className={`border-t border-slate-100 ${l.progressable ? "hover:bg-slate-50" : "opacity-50"}`} data-testid={`wp-line-row-${l.element_code}`}>
                  <td className="px-3 py-2 font-mono">{l.element_code}</td>
                  <td className="px-3 py-2 max-w-[220px] truncate font-bold text-slate-900" title={l.description}>{l.name}{!l.progressable && <span className="ml-1 text-[9px] font-normal text-slate-400 uppercase">({l.line_type || "descriptive"})</span>}</td>
                  <td className="px-3 py-2">{l.uom_name}</td>
                  <td className="px-3 py-2" data-testid={`wp-line-scope-${l.element_code}`}>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.client_scope_status === "Not Approved" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {l.client_scope_status || "Approved"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{l.boq_quantity ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-bold">{l.cumulative_qty}</td>
                  <td className="px-3 py-2 text-right">{l.remaining_qty ?? "—"}</td>
                  <td className={`px-3 py-2 text-right font-bold ${l.progress_pct > 100 ? "text-orange-700" : "text-emerald-800"}`} data-testid={`wp-line-pct-${l.element_code}`}>
                    {l.progress_pct != null ? `${l.progress_pct}%` : "—"}
                  </td>
                  {vv && <td className="px-3 py-2 text-right">{inr(l.client_rate)}</td>}
                  {vv && <td className="px-3 py-2 text-right">{inr(l.executed_value_ex_gst)}</td>}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {l.entry_count > 0 && (
                      <button data-testid={`wp-line-history-${l.element_code}`} onClick={() => setHist(l)}
                        className="text-sky-700 hover:underline inline-flex items-center gap-1 mr-2"><History size={12} /> History</button>
                    )}
                    {canCreate && l.progressable && (
                      <button data-testid={`wp-line-add-${l.element_code}`} onClick={() => setEntryModal({ line: l })}
                        className="text-emerald-800 hover:underline inline-flex items-center gap-1 font-bold"><Plus size={12} /> Add</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            <Paginator pager={linePager} testid="wp-lines-pager" />
          </div>
        </div>
      )}

      {view === "history" && (
        <div className="space-y-2">
          <input data-testid="wp-history-search" className="border border-slate-300 rounded px-2 py-1.5 text-xs w-60"
            placeholder="Search element / remark / user…" value={flt.q}
            onChange={(e) => { setFlt({ ...flt, q: e.target.value }); setRegPage(1); }} />
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full min-w-[820px] text-xs" data-testid="wp-history-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">BOQ Ver</th>
                  <th className="px-3 py-2 text-left">Element</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Prev → New</th>
                  <th className="px-3 py-2 text-right">Cum %</th>
                  {vv && <th className="px-3 py-2 text-right">Amount</th>}
                  <th className="px-3 py-2 text-left">Remark</th>
                  <th className="px-3 py-2 text-left">By</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(reg.rows || []).length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400" data-testid="wp-history-empty">No progress recorded yet.</td></tr>
                )}
                {(reg.rows || []).map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wp-entry-${e.id}`}>
                    <td className="px-3 py-2">{e.progress_date}</td>
                    <td className="px-3 py-2 font-mono">V{e.boq_version_number}</td>
                    <td className="px-3 py-2 font-mono">{e.element_code}</td>
                    <td className="px-3 py-2 text-right font-bold">{e.qty_delta > 0 ? "+" : ""}{e.qty_delta} {e.uom_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{e.previous_cumulative_qty} → {e.new_cumulative_qty}</td>
                    <td className="px-3 py-2 text-right">{e.cumulative_pct != null ? `${e.cumulative_pct}%` : "—"}</td>
                    {vv && <td className="px-3 py-2 text-right">{inr(e.amount_delta_ex_gst)}</td>}
                    <td className="px-3 py-2 max-w-[180px] truncate" title={e.remark}>{e.remark}</td>
                    <td className="px-3 py-2">{e.created_by}</td>
                    <td className="px-3 py-2">
                      {e.entry_kind === "reversal"
                        ? <span className="text-orange-700 font-bold">Reversal</span>
                        : e.entry_kind === "carry_forward"
                        ? <span className="text-sky-700 font-bold" data-testid={`wp-reg-cf-${e.id}`}>Carried Fwd</span>
                        : e.status === "Reversed"
                          ? <span className="inline-flex items-center gap-1 text-orange-700 font-bold" title={e.reversal_reason}><Undo2 size={11} /> Reversed</span>
                          : <span className="text-emerald-700 font-bold">{e.status}</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {e.entry_kind === "progress" && (
                        <button data-testid={`wp-photos-${e.id}`} onClick={() => setPhotosFor(e)}
                          className="text-slate-700 hover:underline inline-flex items-center gap-1 mr-2">
                          <Camera size={12} /> Photos{(e.photos || []).length > 0 ? ` (${(e.photos || []).length})` : ""}
                        </button>
                      )}
                      {canReverse && e.entry_kind === "progress" && e.status === "Posted" && (
                        <button data-testid={`wp-reverse-${e.id}`} onClick={() => setRev(e)}
                          className="text-orange-700 hover:underline inline-flex items-center gap-1"><Undo2 size={12} /> Reverse</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {reg.total > limit && (
            <div className="flex items-center justify-end gap-2 text-xs">
              <button disabled={regPage <= 1} onClick={() => setRegPage(regPage - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
              <span>Page {regPage} / {Math.ceil(reg.total / limit)}</span>
              <button disabled={regPage >= Math.ceil(reg.total / limit)} onClick={() => setRegPage(regPage + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}

      {view === "labour" && (
        <div className="space-y-2">
          <input data-testid="wp-labour-search" value={labourQ} onChange={(e) => setLabourQ(e.target.value)}
            placeholder="Search labour ID, name, mobile, skill, type…"
            className="border border-slate-300 rounded px-2 py-1.5 text-xs w-72" />
          <div className="border border-slate-200 rounded overflow-x-auto">
          <table className="w-full min-w-[720px] text-xs" data-testid="wp-labour-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Labour ID</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Mobile</th>
                <th className="px-3 py-2 text-left">Skill Category</th>
                <th className="px-3 py-2 text-left">Labour Type</th>
                <th className="px-3 py-2 text-left">Assigned From</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLabour.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="wp-labour-empty">
                  {labour === null ? "Loading…" : (labour || []).length === 0
                    ? "No labour assigned to this project."
                    : "No labour matches the search."}</td></tr>
              )}
              {labourPager.slice.map((a) => (
                <tr key={a.assignment_id} className="border-t border-slate-100" data-testid={`wp-labour-row-${a.labour_code}`}>
                  <td className="px-3 py-2 font-mono font-bold">{a.labour_code}</td>
                  <td className="px-3 py-2">{a.name}</td>
                  <td className="px-3 py-2">{a.mobile}</td>
                  <td className="px-3 py-2">{a.skill_category}</td>
                  <td className="px-3 py-2">{a.labour_type}</td>
                  <td className="px-3 py-2">{a.assigned_from}{a.released_on ? ` → ${a.released_on}` : ""}</td>
                  <td className="px-3 py-2">{a.status === "Active"
                    ? <span className="text-emerald-700 font-bold">Active</span>
                    : <span className="text-slate-400">Released</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginator pager={labourPager} testid="wp-labour-pager" />
          </div>
        </div>
      )}

      {view === "attendance" && canAttendance && <LabourAttendanceTab key={`att-${childKey}`} project={project} />}

      {view === "labpay" && canLabourPay && <LabourPaymentsTab key={`lp-${childKey}`} project={project} />}

      {view === "dpr" && canDpr && <DprTab key={`dpr-${childKey}`} project={project} />}

      {entryModal && <EntryModal project={project} lines={linesData.rows || []} initialLine={entryModal.line}
        onClose={() => setEntryModal(null)} onPosted={refreshAll} />}
      {rev && <ReverseModal project={project} entry={rev} onClose={() => setRev(null)} onDone={refreshAll} />}
      {hist && <LineHistoryModal project={project} line={hist} onClose={() => setHist(null)} />}
      {photosFor && <WpPhotosModal project={project} entry={photosFor} canCreate={canCreate}
        onClose={() => setPhotosFor(null)} onChanged={refreshAll} />}
    </div>
  );
}
